import { useState } from 'react'
import { api, ErroApi } from '../lib/api'
import { Marca } from '../componentes/Marca'
import './aceitar-convite.css'

/**
 * Aceitar o convite da equipe.
 *
 * Chega por link, sem sessão — a pessoa pode nem ter conta ainda. É a
 * primeira tela do Clinuma que ela vê, e por isso diz de quem é a
 * clínica antes de pedir qualquer coisa.
 *
 * **Ela define a própria senha.** Quem convidou nunca digita, nunca vê
 * e não consegue recuperar. Feegow e iClinic fazem o contrário — o
 * admin cria login e senha do funcionário — e isso destrói a
 * não-repúdio da assinatura do prontuário: se duas pessoas conhecem a
 * senha, nenhuma evolução tem autor provável.
 */
export function AceitarConvite({ token, aoEntrar }: {
  token: string
  aoEntrar(): void
}) {
  const [nome, setNome] = useState('')
  const [senha, setSenha] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [pronto, setPronto] = useState(false)

  const senhaCurta = senha.length > 0 && senha.length < 10

  async function submeter(e: React.FormEvent) {
    e.preventDefault()
    setErro(null); setEnviando(true)
    try {
      await api('/v1/convites/aceitar', {
        metodo: 'POST',
        corpo: { token, nome: nome.trim(), senha },
      })
      setPronto(true)
    } catch (err) {
      // 410 é o caso mais comum e merece texto próprio: link expirado
      // ou já usado não é erro da pessoa, e a saída é pedir outro.
      setErro(
        err instanceof ErroApi && err.status === 410
          ? 'Este convite não vale mais — ele expira em 72 horas e serve uma vez só. Peça outro a quem administra a clínica.'
          : err instanceof ErroApi ? err.message
          : 'Não foi possível aceitar o convite.',
      )
      setEnviando(false)
    }
  }

  if (pronto) {
    return (
      <main className="acv">
        <div className="acv__caixa cartao">
          <Marca tamanho={30} />
          <h1>Tudo certo</h1>
          <p>
            Sua conta está criada. Entre com o e-mail do convite e a senha que
            você acabou de escolher.
          </p>
          <button className="botao" onClick={aoEntrar}>Ir para o login</button>
        </div>
      </main>
    )
  }

  return (
    <main className="acv">
      <form className="acv__caixa cartao" onSubmit={submeter}>
        <Marca tamanho={30} />
        <div>
          <h1>Você foi convidado</h1>
          <p className="ajuda">
            Crie sua conta para entrar na clínica. Leva menos de um minuto.
          </p>
        </div>

        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        <div className="campo">
          <label htmlFor="ac-nome">Seu nome</label>
          <input id="ac-nome" className="entrada" required autoFocus
                 autoComplete="name" value={nome}
                 onChange={(e) => setNome(e.target.value)} />
          <span className="ajuda">
            É o nome que vai aparecer na agenda e nas assinaturas do prontuário.
          </span>
        </div>

        <div className="campo">
          <label htmlFor="ac-senha">Crie uma senha</label>
          <input id="ac-senha" className="entrada" type="password" required
                 autoComplete="new-password" minLength={10}
                 aria-invalid={senhaCurta}
                 value={senha} onChange={(e) => setSenha(e.target.value)} />
          <span className={senhaCurta ? 'ajuda ajuda--erro' : 'ajuda'}>
            {senhaCurta
              ? `${senha.length} de 10 caracteres, no mínimo.`
              : 'Mínimo de 10 caracteres. Só você vai saber.'}
          </span>
        </div>

        <button className="botao" type="submit"
                disabled={enviando || !nome.trim() || senha.length < 10}>
          {enviando ? 'Criando…' : 'Criar minha conta'}
        </button>

        <p className="ajuda acv__rodape">
          Quem convidou você escolheu o seu papel na clínica, mas não vê nem
          define sua senha.
        </p>
      </form>
    </main>
  )
}
